import { Navigate } from 'react-router-dom';
import {
  bg, line, tx, font, accent, space, radius, attrColour,
} from '../theme';
import { useClub } from './ClubContext';
import ClubCrest from './ClubCrest';
import {
  fmtMoney, ageFromDob, fmtContract, groupFor, GROUP_ORDER, GROUP_LABEL,
} from './gameUtils';

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: tx.muted, fontFamily: font.display,
      fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

const TH = {
  fontFamily: "'Chakra Petch', sans-serif", fontSize: 10, fontWeight: 700,
  letterSpacing: '0.1em', textTransform: 'uppercase', color: tx.faint,
  textAlign: 'left', padding: '0 12px 8px', whiteSpace: 'nowrap',
};
const TD = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, color: tx.secondary,
  padding: '10px 12px', borderTop: `1px solid ${line.DEFAULT}`,
  whiteSpace: 'nowrap',
};

export default function Squad() {
  const { club, squad, status } = useClub();

  if (status === 'notfound') return <Navigate to="/hub" replace />;
  if (status === 'loading') return <Screen>Loading squad</Screen>;
  if (status === 'error') return <Screen>Couldn&rsquo;t reach the world database</Screen>;
  if (status === 'empty' || !club) return <Screen>Club not found</Screen>;

  const list = squad || [];

  return (
    <div style={{ padding: space.s5, maxWidth: 1400 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: space.s3,
        marginBottom: space.s5,
      }}>
        <ClubCrest club={club} size={40} />
        <div>
          <h1 style={{
            fontFamily: font.display, fontSize: 24, fontWeight: 700,
            letterSpacing: '0.01em', margin: 0,
          }}>
            Squad
          </h1>
          <div style={{ fontFamily: font.data, fontSize: 12, color: tx.muted, marginTop: 2 }}>
            {club.name} &middot; {list.length} players
          </div>
        </div>
      </div>

      {GROUP_ORDER.map((g) => {
        const rows = list
          .filter((p) => groupFor(p.primary_position) === g)
          .sort((a, b) => Number(b.current_ability) - Number(a.current_ability));
        if (rows.length === 0) return null;
        return (
          <section key={g} style={{
            marginBottom: space.s5, background: bg.surface,
            border: `1px solid ${line.DEFAULT}`, borderRadius: radius.lg,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: `${space.s3}px ${space.s4}px`,
              borderBottom: `1px solid ${line.DEFAULT}`,
              display: 'flex', alignItems: 'center', gap: space.s3,
            }}>
              <span style={{
                fontFamily: font.display, fontSize: 12, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: accent.lime,
              }}>
                {GROUP_LABEL[g]}
              </span>
              <span style={{ fontFamily: font.data, fontSize: 11, color: tx.faint }}>
                {rows.length}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, paddingLeft: 16 }}>Pos</th>
                    <th style={TH}>Name</th>
                    <th style={TH}>Age</th>
                    <th style={TH}>Nat</th>
                    <th style={TH}>Archetype</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Ability</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Value</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Wage</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Contract</th>
                    <th style={{ ...TH, paddingRight: 16 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const age = ageFromDob(p.dob);
                    const injured = /injur/i.test(p.status || '');
                    return (
                      <tr key={p.id}>
                        <td style={{ ...TD, paddingLeft: 16, color: tx.muted }}>{p.primary_position}</td>
                        <td style={{ ...TD, color: tx.primary, fontFamily: font.display, fontWeight: 600, fontSize: 13.5 }}>
                          {p.first_name} {p.last_name}
                        </td>
                        <td style={TD}>{age != null ? age : '—'}</td>
                        <td style={TD}>{p.nationality || '—'}</td>
                        <td style={{ ...TD, color: tx.muted }}>{p.archetype || '—'}</td>
                        <td style={{
                          ...TD, textAlign: 'right', fontWeight: 700,
                          color: attrColour(Number(p.current_ability)),
                        }}>
                          {p.current_ability || '—'}
                        </td>
                        <td style={{ ...TD, textAlign: 'right' }}>{fmtMoney(p.market_value)}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>
                          {p.wage ? `${fmtMoney(p.wage)}/wk` : '—'}
                        </td>
                        <td style={{ ...TD, textAlign: 'right', color: tx.muted }}>{fmtContract(p.contract_expiry)}</td>
                        <td style={{
                          ...TD, paddingRight: 16,
                          color: injured ? accent.amber : tx.muted,
                        }}>
                          {p.status || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {list.length === 0 && <Screen>No players on the books</Screen>}
    </div>
  );
}
